import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, ChevronDown, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Link } from "wouter";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

export interface MobileBrowseOption {
  name: string;
  active: boolean;
  href: string;
}

interface MobileBrowseSheetProps {
  heading: string;
  open: boolean;
  resultCount: number | null;
  regions: readonly MobileBrowseOption[];
  categories: readonly MobileBrowseOption[];
  onOpenChange: (open: boolean) => void;
  onRegionSelect: (region: string) => void;
  onCategorySelect: (category: string) => void;
}

type BrowseMode = "regions" | "categories";

function formatResultCount(count: number | null) {
  if (count == null) return "Local events ready to explore";
  return `${count.toLocaleString()} ${count === 1 ? "event" : "events"}`;
}

export function MobileBrowseSheet({
  heading,
  open,
  resultCount,
  regions,
  categories,
  onOpenChange,
  onRegionSelect,
  onCategorySelect,
}: MobileBrowseSheetProps) {
  const isMobile = useIsMobile();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const activeRegion = regions.find((region) => region.active);
  const activeCategory = categories.find((category) => category.active);
  const activeCategoryName = activeCategory?.name ?? null;
  const [mode, setMode] = useState<BrowseMode>(activeCategory ? "categories" : "regions");

  useEffect(() => {
    if (!open) return;
    setMode(activeCategoryName ? "categories" : "regions");
  }, [activeCategoryName, open]);

  const activeLabel = activeCategory?.name ?? activeRegion?.name ?? "All regions & categories";
  const options = useMemo(
    () => (mode === "regions" ? regions : categories),
    [categories, mode, regions],
  );

  const handleOptionClick = (
    event: MouseEvent<HTMLAnchorElement>,
    option: MobileBrowseOption,
  ) => {
    event.preventDefault();
    if (mode === "regions") {
      onRegionSelect(option.name);
      return;
    }
    onCategorySelect(option.name);
  };

  return (
    <div className="md:hidden">
      <DialogPrimitive.Root
        open={isMobile && open}
        onOpenChange={(nextOpen) => {
          if (isMobile) onOpenChange(nextOpen);
        }}
      >
        <DialogPrimitive.Trigger asChild>
          <button
            type="button"
            className="flex min-h-[56px] w-full items-center gap-3 rounded-[16px] border border-[#d7e4d9] bg-[linear-gradient(180deg,rgba(244,248,243,0.98),rgba(255,255,255,0.98))] px-3 py-2.5 text-left shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-[background-color,border-color,box-shadow] hover:border-[#bfd4c4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
            aria-label={`Browse regions and categories. Current selection: ${activeLabel}. ${formatResultCount(resultCount)}.`}
            data-testid="homepage-mobile-browse-trigger"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold leading-tight text-foreground">
                {activeLabel}
              </span>
              <span className="mt-1 block text-[11px] leading-none text-muted-foreground">
                {formatResultCount(resultCount)}
              </span>
            </span>
            <span className="inline-flex min-h-[34px] shrink-0 items-center gap-1 rounded-full border border-[#9FE1CB] bg-[#edf8f3] px-3 py-1 text-[12px] font-semibold leading-none text-[#167a5d]">
              {activeRegion || activeCategory ? "Change" : "Browse"}
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
          </button>
        </DialogPrimitive.Trigger>

        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-[#16231e]/45 backdrop-blur-[1px] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none" />
          <DialogPrimitive.Content
            className="fixed inset-x-0 bottom-0 z-[80] flex max-h-[min(82dvh,720px)] flex-col rounded-t-[24px] border border-b-0 border-[#d9d0c4] bg-[#fffdf8] shadow-[0_-12px_36px_rgba(22,35,30,0.16)] outline-none data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom motion-reduce:animate-none"
            aria-describedby="mobile-browse-description"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              window.requestAnimationFrame(() => closeButtonRef.current?.focus());
            }}
            data-testid="homepage-mobile-browse-sheet"
          >
            <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-[#d8d2c8]" aria-hidden="true" />
            <div className="flex items-start gap-3 px-4 pb-3 pt-3">
              <div className="min-w-0 flex-1">
                <DialogPrimitive.Title className="text-[20px] font-semibold tracking-tight text-foreground">
                  Browse local events
                </DialogPrimitive.Title>
                <DialogPrimitive.Description
                  id="mobile-browse-description"
                  className="mt-1 text-[12px] leading-5 text-muted-foreground"
                >
                  Choose one option. We’ll update the event list and take you there.
                </DialogPrimitive.Description>
              </div>
              <DialogPrimitive.Close asChild>
                <button
                  ref={closeButtonRef}
                  type="button"
                  className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full border border-[#d7e4d9] bg-white text-[#335547] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
                  aria-label="Close Browse"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </DialogPrimitive.Close>
            </div>

            <div className="px-4 pb-3">
              <div
                className="grid grid-cols-2 rounded-[14px] bg-[#edf1eb] p-1"
                role="group"
                aria-label="Browse by"
              >
                {(["regions", "categories"] as const).map((browseMode) => (
                  <button
                    key={browseMode}
                    type="button"
                    onClick={() => setMode(browseMode)}
                    className={cn(
                      "min-h-[42px] rounded-[11px] px-3 text-[13px] font-semibold capitalize transition-[background-color,color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-1",
                      mode === browseMode
                        ? "bg-white text-[#23483b] shadow-sm"
                        : "text-[#68766f]",
                    )}
                    aria-pressed={mode === browseMode}
                    data-testid={`homepage-mobile-browse-tab-${browseMode}`}
                  >
                    {browseMode}
                  </button>
                ))}
              </div>
            </div>

            <div className="min-h-0 overflow-y-auto overscroll-contain px-4 pb-3">
              <p className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-muted-foreground">
                {mode === "regions" ? "Choose a region" : "Choose a category"}
              </p>
              <div className={cn("grid gap-2", mode === "regions" ? "grid-cols-2" : "grid-cols-1")}>
                {options.map((option) => (
                  <Link
                    key={option.name}
                    href={option.href}
                    onClick={(event) => handleOptionClick(event, option)}
                    className={cn(
                      "flex min-h-[48px] min-w-0 items-center justify-between gap-2 rounded-[14px] border px-3 py-2.5 text-[13px] font-semibold leading-snug transition-[background-color,border-color,box-shadow,transform] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2",
                      option.active
                        ? "border-[#8fc3a0] bg-[#e7f3e8] text-[#23483b] shadow-[var(--brutal-shadow-sm)]"
                        : "border-[#d7e4d9] bg-white text-[#335547] hover:border-[#bfd4c4] hover:bg-[#f3f8f3]",
                    )}
                    aria-current={option.active ? "page" : undefined}
                    data-testid={`homepage-mobile-browse-option-${mode}-${option.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="min-w-0 break-words">{option.name}</span>
                    {option.active && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
                  </Link>
                ))}
              </div>
            </div>

            <div className="border-t border-[#ece4d7] px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-3">
              <p className="text-center text-[11px] leading-4 text-muted-foreground">
                Showing options for {heading}. Use Filters for more choices.
              </p>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
}
